import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Button,
  Form,
  Badge,
  Table,
  Modal,
  Spinner,
  Alert,
  Row,
  Col,
  Dropdown,
  Pagination,
  InputGroup,
} from "react-bootstrap";
import {
  IconTicket,
  IconPlus,
  IconSearch,
  IconRefresh,
  IconEye,
  IconEdit,
  IconUserCheck,
  IconClock,
  IconPaperclip,
  IconFilter,
} from "@tabler/icons-react";
import UserContext from "../../../contexts/UserContext";
import { isClientPlan } from "../../../utils/PlanUtils";
import { 
  getTickets,
  createTicket,
  updateTicket,
  assignTicket,
  getAvailableAgents
} from "../../../services/SupportServices";

const TicketSystem = ({ config, isClientView = false }) => {
  const { user } = useContext(UserContext);
  
  // Estados principais
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Estados de UI
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  
  // Estados de filtros e busca
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Estados do formulário
  const [newTicketForm, setNewTicketForm] = useState({
    title: "",
    description: "",
    category: "Problema técnico",
    attachments: null
  });
  
  const [editForm, setEditForm] = useState({
    status: ""
  });
  
  const [assignForm, setAssignForm] = useState({
    agent_id: ""
  });
  
  // Estados para agentes
  const [availableAgents, setAvailableAgents] = useState([]);

  useEffect(() => {
    loadTickets();
    loadAgents();
  }, [searchTerm, statusFilter, categoryFilter, priorityFilter]);

  // ==========================================
  // FUNÇÕES DE API
  // ==========================================

  const loadTickets = async () => {
    try {
      setIsLoading(true);
      
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (statusFilter !== "all") filters.status = statusFilter;
      if (categoryFilter !== "all") filters.category = categoryFilter;
      if (priorityFilter !== "all") filters.priority = priorityFilter;
      
      // Se for visualização de cliente, filtrar apenas tickets do próprio usuário
      if (isClientView) {
        filters.customer_id = user?.id;
      }
      
      const result = await getTickets(filters);
      
      if (result.success) {
        console.log("✅ Tickets carregados no componente:", result.data);
        console.log("🔍 Status dos tickets:", result.data?.map(t => ({ id: t.id, status: t.status })));
        setTickets(result.data || []);
      } else if (result.isApiUnavailable) {
        // API não está disponível, não mostrar erro
        console.log("⚠️ API não disponível para tickets");
        setTickets([]);
      } else {
        console.error("❌ Erro ao carregar tickets:", result.error);
        setTickets([]);
      }
    } catch (error) {
      console.warn("Erro inesperado ao carregar tickets:", error.message);
      setTickets([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const result = await getAvailableAgents();
      
      if (result.success) {
        setAvailableAgents(result.data);
      } else if (result.isApiUnavailable) {
        // API não está disponível, não mostrar erro
        setAvailableAgents([]);
      } else {
        console.error("Erro ao carregar agentes:", result.error);
        setAvailableAgents([]);
      }
    } catch (error) {
      console.warn("Erro inesperado ao carregar agentes:", error.message);
      setAvailableAgents([]);
    }
  };

  const createTicketHandler = async () => {
    try {
      setIsCreating(true);
      
      const result = await createTicket(newTicketForm);
      
      if (result.success) {
        setTickets(prev => [result.data, ...prev]);
        setShowCreateModal(false);
        resetNewTicketForm();
      } else if (result.isApiUnavailable) {
        console.warn("API de tickets não está disponível");
        // Não mostrar erro para não confundir usuário
      } else {
        console.error("Erro ao criar ticket:", result.error);
      }
    } catch (error) {
      console.warn("Erro inesperado ao criar ticket:", error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const updateTicketHandler = async (ticketId) => {
    try {
      setIsUpdating(true);
      
      const result = await updateTicket(ticketId, editForm);
      
      if (result.success) {
        setTickets(prev => prev.map(ticket => 
          ticket.id === ticketId ? { ...ticket, ...result.data } : ticket
        ));
        setShowEditModal(false);
      } else if (result.isApiUnavailable) {
        console.warn("API de tickets não está disponível");
      } else {
        console.error("Erro ao atualizar ticket:", result.error);
      }
    } catch (error) {
      console.warn("Erro inesperado ao atualizar ticket:", error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const assignTicketHandler = async (ticketId) => {
    try {
      setIsUpdating(true);
      
      const result = await assignTicket(ticketId, assignForm.agent_id);
      
      if (result.success) {
        loadTickets();
        setShowAssignModal(false);
      } else if (result.isApiUnavailable) {
        console.warn("API de tickets não está disponível");
      } else {
        console.error("Erro ao atribuir ticket:", result.error);
      }
    } catch (error) {
      console.warn("Erro inesperado ao atribuir ticket:", error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  // ==========================================
  // FUNÇÕES AUXILIARES
  // ==========================================

  const resetNewTicketForm = () => {
    setNewTicketForm({
      title: "",
      description: "",
      category: "Problema técnico",
      attachments: null
    });
  };

  const getPriorityBadge = (priority) => {
    if (!priority) {
      return (
        <Badge bg="secondary">
          Não definida
        </Badge>
      );
    }

    const variants = {
      low: "success",
      medium: "warning", 
      high: "danger",
      urgent: "dark"
    };
    
    const labels = {
      low: "Baixa",
      medium: "Média",
      high: "Alta", 
      urgent: "Urgente"
    };
    
    return (
      <Badge bg={variants[priority] || "secondary"}>
        {labels[priority] || priority}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    console.log("🏷️ getStatusBadge chamado com status:", status, typeof status);
    
    const variants = {
      "Aberto": "primary",
      "Em andamento": "warning",
      "Resolvido": "success",
      "Fechado": "secondary"
    };
    
    const variant = variants[status] || "secondary";
    console.log("🎨 Variante escolhida:", variant, "para status:", status);
    
    return (
      <Badge bg={variant} title={`Status original: "${status}" | Variante: ${variant}`}>
        {status} [TICKET]
      </Badge>
    );
  };

  const filteredTickets = tickets.filter(ticket => {
    if (searchTerm && !ticket.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (statusFilter !== "all" && ticket.status !== statusFilter) {
      return false;
    }
    if (categoryFilter !== "all" && ticket.category !== categoryFilter) {
      return false;
    }
    if (priorityFilter !== "all" && (ticket.priority || "").toLowerCase() !== priorityFilter.toLowerCase()) {
      return false;
    }
    return true;
  });

  // Paginação
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  // ==========================================
  // RENDERIZAÇÃO
  // ==========================================

  if (!config || !config.is_active) {
    return (
      <Alert variant="warning">
        <h5>Sistema de Tickets Inativo</h5>
        <p>Configure e ative o sistema de suporte para usar os tickets.</p>
      </Alert>
    );
  }

  if (config.support_type === "chat") {
    return (
      <Alert variant="info">
        <h5>Tickets Não Disponível</h5>
        <p>Sua configuração atual permite apenas suporte via chat.</p>
      </Alert>
    );
  }

  return (
    <div>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <IconTicket className="me-2" />
              {isClientView ? "Meus Tickets" : "Sistema de Tickets"}
            </h5>

            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  console.log("🔄 Forçando refresh de tickets...");
                  setTickets([]); // Limpar cache local
                  loadTickets();
                }}
                disabled={isLoading}
              >
                <IconRefresh size={16} />
              </Button>

              {(isClientView || isClientPlan(user?.plan_id)) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowCreateModal(true)}
                >
                  <IconPlus size={16} className="me-1" />
                  {isClientView ? "Criar Ticket" : "Novo Ticket"}
                </Button>
              )}
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {/* Filtros e Busca - Simplificados para cliente */}
          {!isClientView ? (
            <Row className="mb-4">
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <IconSearch size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>

              <Col md={2}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos Status</option>
                  <option value="Aberto">Aberto</option>
                  <option value="Em andamento">Em andamento</option>
                  <option value="Resolvido">Resolvido</option>
                  <option value="Fechado">Fechado</option>
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">Todas Categorias</option>
                  <option value="Problema técnico">Problema técnico</option>
                  <option value="Dúvida">Dúvida</option>
                  <option value="Solicitação">Solicitação</option>
                  <option value="Reclamação">Reclamação</option>
                  <option value="Sugestão">Sugestão</option>
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <option value="all">Todas Prioridades</option>
                  <option value="low">Baixa</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </Form.Select>
              </Col>
            </Row>
          ) : (
            <Row className="mb-4">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <IconSearch size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar nos meus tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos Status</option>
                  <option value="Aberto">Aberto</option>
                  <option value="Em andamento">Em andamento</option>
                  <option value="Resolvido">Resolvido</option>
                  <option value="Fechado">Fechado</option>
                </Form.Select>
              </Col>
            </Row>
          )}

          {/* Tabela de Tickets */}
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Carregando tickets...</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-4">
              <IconTicket size={48} className="text-muted mb-3" />
              <h5>{isClientView ? "Você ainda não tem tickets" : "Nenhum ticket encontrado"}</h5>
              <p className="text-muted">
                {searchTerm ||
                statusFilter !== "all" ||
                categoryFilter !== "all" ||
                priorityFilter !== "all"
                  ? "Tente ajustar os filtros de busca"
                  : isClientView ? "Crie seu primeiro ticket de suporte" : "Nenhum ticket foi criado ainda"}
              </p>
              {isClientView && (
                <Button
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  Criar Primeiro Ticket
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Título</th>
                    {!isClientView && <th>Cliente</th>}
                    <th>Categoria</th>
                    <th>Status</th>
                    {!isClientView && <th>Agente</th>}
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((ticket, index) => (
                    <tr key={index}>
                      <td>#{ticket.id}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {ticket.title}
                          {ticket.attachments && (
                            <IconPaperclip size={14} className="text-muted" />
                          )}
                        </div>
                      </td>
                      {!isClientView && <td>{ticket.user_name || ticket.customer_name || "N/A"}</td>}
                      <td>
                        <Badge bg="info" className="small">
                          {ticket.category}
                        </Badge>
                      </td>
                      <td>{getStatusBadge(ticket.status)}</td>
                      {!isClientView && (
                        <td>
                          {ticket.agent_name ? (
                            <small>{ticket.agent_name}</small>
                          ) : (
                            <small className="text-muted">Não atribuído</small>
                          )}
                        </td>
                      )}
                      <td>
                        <small className="text-muted">
                          <IconClock size={12} className="me-1" />
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setShowViewModal(true);
                            }}
                          >
                            <IconEye size={14} />
                          </Button>

                          {/* Botões administrativos - apenas para planos avançados */}
                          {!isClientView && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                  setSelectedTicket(ticket);
                                  setEditForm({
                                    status: ticket.status
                                  });
                                  setShowEditModal(true);
                                }}
                              >
                                <IconEdit size={14} />
                              </Button>

                              {!ticket.agent && (
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  onClick={() => {
                                    setSelectedTicket(ticket);
                                    setAssignForm({ agent_id: "" });
                                    setShowAssignModal(true);
                                  }}
                                >
                                  <IconUserCheck size={14} />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center">
                  <Pagination>
                    <Pagination.First
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    />
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => prev - 1)}
                    />

                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === currentPage}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      }
                      return null;
                    })}

                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                    />
                    <Pagination.Last
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal para Criar Ticket */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{isClientView ? "Criar Ticket de Suporte" : "Novo Ticket de Suporte"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Título *</Form.Label>
              <Form.Control
                type="text"
                value={newTicketForm.title}
                onChange={(e) =>
                  setNewTicketForm((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                placeholder="Descreva brevemente o problema"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Categoria</Form.Label>
              <Form.Select
                value={newTicketForm.category}
                onChange={(e) =>
                  setNewTicketForm((prev) => ({
                    ...prev,
                    category: e.target.value,
                  }))
                }
              >
                <option value="Problema técnico">Problema técnico</option>
                <option value="Dúvida">Dúvida</option>
                <option value="Solicitação">Solicitação</option>
                <option value="Reclamação">Reclamação</option>
                <option value="Sugestão">Sugestão</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descrição *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={newTicketForm.description}
                onChange={(e) =>
                  setNewTicketForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Descreva detalhadamente o problema ou solicitação"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Anexos (opcional)</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) =>
                  setNewTicketForm((prev) => ({
                    ...prev,
                    attachments: e.target.files[0],
                  }))
                }
                accept="image/*,.pdf,.doc,.docx"
              />
              <Form.Text className="text-muted">
                Formatos aceitos: imagens, PDF, Word. Tamanho máximo: 10MB
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={createTicketHandler}
            disabled={
              !newTicketForm.title.trim() ||
              !newTicketForm.description.trim() ||
              isCreating
            }
          >
            {isCreating && <Spinner size="sm" className="me-2" />}
            Criar Ticket
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para Visualizar Ticket */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Ticket #{selectedTicket?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTicket && (
            <div>
              <Row className="mb-3">
                <Col md={8}>
                  <h5>{selectedTicket.title}</h5>
                </Col>
                <Col md={4} className="text-end">
                  {getStatusBadge(selectedTicket.status)}{" "}
                  {getPriorityBadge(selectedTicket.priority)}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <small className="text-muted">Cliente:</small>
                  <br />
                  <strong>{selectedTicket.user_name || selectedTicket.customer_name || "N/A"}</strong>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Categoria:</small>
                  <br />
                  <Badge bg="info">{selectedTicket.category}</Badge>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <small className="text-muted">Criado em:</small>
                  <br />
                  {new Date(selectedTicket.created_at).toLocaleString()}
                </Col>
                <Col md={6}>
                  <small className="text-muted">Agente:</small>
                  <br />
                  {selectedTicket.agent_name || "Não atribuído"}
                </Col>
              </Row>

              <div className="mb-3">
                <small className="text-muted">Descrição:</small>
                <div className="border rounded p-3 mt-2 bg-light">
                  {selectedTicket.description}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para Editar Ticket */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Ticket #{selectedTicket?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={editForm.status}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    status: e.target.value,
                  }))
                }
              >
                <option value="Aberto">Aberto</option>
                <option value="Em andamento">Em andamento</option>
                <option value="Resolvido">Resolvido</option>
                <option value="Fechado">Fechado</option>
              </Form.Select>
            </Form.Group>

          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => updateTicketHandler(selectedTicket.id)}
            disabled={isUpdating}
          >
            {isUpdating && <Spinner size="sm" className="me-2" />}
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para Atribuir Agente */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Atribuir Agente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Selecionar Agente</Form.Label>
              <Form.Select
                value={assignForm.agent_id}
                onChange={(e) => setAssignForm({ agent_id: e.target.value })}
              >
                <option value="">Selecione um agente...</option>
                {availableAgents.map((agent) => (
                  <option key={agent.id} value={agent.user}>
                    {agent.user_name} -{" "}
                    {agent.specialties?.join(", ") || "Geral"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={() => assignTicketHandler(selectedTicket.id)}
            disabled={!assignForm.agent_id || isUpdating}
          >
            {isUpdating && <Spinner size="sm" className="me-2" />}
            Atribuir Agente
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TicketSystem;